import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
  Breadcrumbs,
  Link
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";

const LeaveApprovalPage = () => {
  const [leaves, setLeaves] = useState([]);
  const [error, setError] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("employee");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Dummy data for testing leave requests with leave balance
  const dummyLeaves = [
    {
      id: 1,
      employee: { first_name: "John", last_name: "Doe", leave_balance: 10 },
      leave_type: "Paid",
      leave_start_date: "2025-01-01",
      leave_end_date: "2025-01-05",
      reason: "Vacation",
      approval_status: true
    },
    {
      id: 2,
      employee: { first_name: "Jane", last_name: "Smith", leave_balance: 8 },
      leave_type: "Unpaid",
      leave_start_date: "2025-02-10",
      leave_end_date: "2025-02-12",
      reason: "Medical",
      approval_status: false
    },
    {
      id: 3,
      employee: {
        first_name: "Alice",
        last_name: "Johnson",
        leave_balance: 12
      },
      leave_type: "Paid",
      leave_start_date: "2025-03-01",
      leave_end_date: "2025-03-03",
      reason: "Family Event",
      approval_status: null
    },
    {
      id: 4,
      employee: { first_name: "Bob", last_name: "Davis", leave_balance: 5 },
      leave_type: "Paid",
      leave_start_date: "2025-04-05",
      leave_end_date: "2025-04-07",
      reason: "Personal",
      approval_status: true
    },
    {
      id: 5,
      employee: { first_name: "Emma", last_name: "White", leave_balance: 7 },
      leave_type: "Sick",
      leave_start_date: "2025-05-01",
      leave_end_date: "2025-05-03",
      reason: "Flu",
      approval_status: null
    },
    {
      id: 6,
      employee: {
        first_name: "Michael",
        last_name: "Brown",
        leave_balance: 10
      },
      leave_type: "Paid",
      leave_start_date: "2025-06-15",
      leave_end_date: "2025-06-17",
      reason: "Vacation",
      approval_status: true
    },
    {
      id: 7,
      employee: { first_name: "Sophia", last_name: "Taylor", leave_balance: 6 },
      leave_type: "Paid",
      leave_start_date: "2025-07-20",
      leave_end_date: "2025-07-23",
      reason: "Conference",
      approval_status: false
    },
    {
      id: 8,
      employee: {
        first_name: "David",
        last_name: "Martinez",
        leave_balance: 15
      },
      leave_type: "Unpaid",
      leave_start_date: "2025-08-05",
      leave_end_date: "2025-08-10",
      reason: "Personal",
      approval_status: null
    }
  ];

  // Simulate fetching leaves from an API by setting the dummy data
  useEffect(() => {
    setLeaves(dummyLeaves);
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortLeaves = (array) => {
    return array.sort((a, b) => {
      if (orderBy === "employee") {
        const employeeA = `${a.employee.first_name} ${a.employee.last_name}`;
        const employeeB = `${b.employee.first_name} ${b.employee.last_name}`;
        if (employeeA < employeeB) return order === "asc" ? -1 : 1;
        if (employeeA > employeeB) return order === "asc" ? 1 : -1;
      } else if (orderBy === "leave_balance") {
        return order === "asc"
          ? a.employee.leave_balance - b.employee.leave_balance
          : b.employee.leave_balance - a.employee.leave_balance;
      } else if (orderBy === "leave_start_date") {
        return order === "asc"
          ? new Date(a.leave_start_date) - new Date(b.leave_start_date)
          : new Date(b.leave_start_date) - new Date(a.leave_start_date);
      }
      return 0;
    });
  };

  const handleApproval = (leaveId, status) => {
    try {
      setLeaves(
        leaves.map((leave) =>
          leave.id === leaveId ? { ...leave, approval_status: status } : leave
        )
      );
      setError("");
    } catch (error) {
      console.error("Error updating leave approval:", error);
      setError("Error updating leave approval. Please try again.");
    }
  };

  const getStatusText = (approvalStatus) => {
    if (approvalStatus === true) return { text: "Approved", color: "green" };
    if (approvalStatus === false) return { text: "Rejected", color: "red" };
    return { text: "Pending", color: "orange" };
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ padding: 3, backgroundColor: "#fff" }}>
      {/* Background for the whole page */}
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 2 }}>
        <Link color="inherit" href="/">
          Home
        </Link>
        <Link color="inherit" href="/leave-approval">
          Leave Approval
        </Link>
        <Typography color="textPrimary">Pending Requests</Typography>
      </Breadcrumbs>
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        Leave Approval
      </Typography>
      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {error}
        </Alert>
      )}
      {/* Table for Leave Requests */}
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: "white",
          "& .MuiTableCell-root": { padding: "4px 8px" },
        }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: "black", color: "white" }}>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  style={{ color: "#fff" }}
                  active={orderBy === "employee"}
                  direction={orderBy === "employee" ? order : "asc"}
                  onClick={() => handleRequestSort("employee")}
                  sx={{
                    color: "#fff",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff",
                    },
                  }}
                >
                  Employee
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>Leave Type</TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ color: "#fff" }}
                  active={orderBy === "leave_start_date"}
                  direction={orderBy === "leave_start_date" ? order : "asc"}
                  onClick={() => handleRequestSort("leave_start_date")}
                  sx={{
                    color: "#fff",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff",
                    },
                  }}
                >
                  Leave Dates
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>Reason</TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ color: "#fff" }}
                  active={orderBy === "leave_balance"}
                  direction={orderBy === "leave_balance" ? order : "asc"}
                  onClick={() => handleRequestSort("leave_balance")}
                  sx={{
                    color: "#fff",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff",
                    },
                  }}
                >
                  Leave Balance
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>Status</TableCell>
              <TableCell sx={{ color: "#fff" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortLeaves(leaves)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((leave, index) => {
                const { text, color } = getStatusText(leave.approval_status);
                return (
                  <TableRow
                    key={leave.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#f1f1f1" : "white",
                    }}
                  >
                    <TableCell>
                      {leave.employee.first_name} {leave.employee.last_name}
                    </TableCell>
                    <TableCell>{leave.leave_type}</TableCell>
                    <TableCell>
                      {leave.leave_start_date} to {leave.leave_end_date}
                    </TableCell>
                    <TableCell>{leave.reason}</TableCell>
                    <TableCell>{leave.employee.leave_balance} days</TableCell>
                    <TableCell sx={{ color: color }}>{text}</TableCell>
                    <TableCell>
                      <IconButton
                        color="success"
                        onClick={() => handleApproval(leave.id, true)}
                        disabled={leave.approval_status}
                        sx={{ marginRight: 1 }}
                      >
                        <CheckCircle />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleApproval(leave.id, false)}
                        disabled={leave.approval_status === false}
                      >
                        <Cancel />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={leaves.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default LeaveApprovalPage;
